import { useSpring } from "@react-spring/web";
import { atom, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useMemo } from "react";
import { useEvent, useMount } from "react-use";
import { useDrag } from "react-use-gesture";
import useWindowSize from "utils/customHooks/useWindowSize";
import { getIsTouchDevice } from "utils/getIsTouchDevice";

export const isFlightDebuggerOpenAtom = atomWithStorage(
  "atom:isFlightDebuggerOpen",
  false
);

const NETWORK_DEBUGGER_HIGHLIGHT_CLASS = "highlightFromFlightDebugger";
const DEBUGGER_MOBILE_WIDTH = 600;
export const DEBUGGER_HEADER_HEIGHT = 42;
const MIN_VISIBLE_PX = { y: DEBUGGER_HEADER_HEIGHT / 2, x: 60 };
export const DEBUGGER_MEDIA_QUERY = `@media only screen and (max-width: ${DEBUGGER_MOBILE_WIDTH}px)`;
const DEBUGGER_PADDING_BOTTOM = 24;

export type PopupDataType = {
  networkName: string | null;
  data: any;
  viewStore?: boolean;
  CustomComponent?: any;
  iframeUrl?: string | null;
  popupId?: string;
};

export type DebuggerStateType = {
  isDebuggerOpen: boolean;
  collapsed: boolean;
  isFlagsEditorOpen: boolean;
  selectedServerName: string;
};

const debuggerStateAtom = atom<DebuggerStateType>({
  isDebuggerOpen: false,
  collapsed: false,
  isFlagsEditorOpen: false,
  selectedServerName: "",
});

export type DebuggerPopupStateType = {
  isPopupOpen: boolean;
  isPreview: boolean;
  collapsed: boolean;
  selectedServerName: string;
  popupData: PopupDataType | null;
};
const debuggerPopupStateAtom = atom<DebuggerPopupStateType>({
  isPopupOpen: false,
  isPreview: true,
  collapsed: false,
  selectedServerName: "",
  popupData: null,
});

export function useDebuggerState(): [
  DebuggerStateType,
  React.Dispatch<React.SetStateAction<DebuggerStateType>>,
] {
  return useAtom(debuggerStateAtom);
}
export function useDebuggerPopupState(): [
  DebuggerPopupStateType,
  React.Dispatch<React.SetStateAction<DebuggerPopupStateType>>,
] {
  return useAtom(debuggerPopupStateAtom);
}

export enum ControlsAnchor {
  TOP_LEFT = "top_left",
  TOP_RIGHT = "top_right",
  BOTTOM_LEFT = "bottom_left",
  BOTTOM_RIGHT = "bottom_right",
}

export const braceAtom = atom<any>("atoms:brace");

/** Highlight elements by css selector with a bright border
 */
export function highlightElements(serverName: string) {
  // select all elements with data-server-names
  const nodes = document.querySelectorAll(
    `[data-server-names]`
  ) as unknown as HTMLElement[];
  // of those, select those with matching server name
  const matchingNodes = Array.from(nodes).filter(node =>
    Boolean(
      // check for each server name on an element: is it equal to the one we're highlighting?
      node?.dataset?.serverNames
        ?.split(",")
        // must be === equal, not just .includes() (which would return true for partial matches)
        .reduce((acc, cur) => acc || cur === serverName, false)
    )
  );

  matchingNodes.forEach(node =>
    node.classList.add(NETWORK_DEBUGGER_HIGHLIGHT_CLASS)
  );
}

/** Unhighlight all highlighted elements
 */
export function unhighlightAllElements() {
  const nodes = document.querySelectorAll(
    `.${NETWORK_DEBUGGER_HIGHLIGHT_CLASS}`
  );
  nodes.forEach(node =>
    node.classList.remove(NETWORK_DEBUGGER_HIGHLIGHT_CLASS)
  );
}
export function getKb(data: any): number | null {
  return !TextEncoder
    ? null
    : new TextEncoder().encode(JSON.stringify(data)).length / 1024;
}

type Position = { x: number; y: number };

export const DEBUGGER_PADDING = { x: 32, y: 32 };

function getStartPosition({
  anchor,
  windowWidth,
  debuggerWidth,
}: {
  anchor: ControlsAnchor;
  windowWidth: number;
  debuggerWidth: number;
}): Position {
  return {
    [ControlsAnchor.TOP_LEFT]: { x: 0, y: 0 },
    [ControlsAnchor.TOP_RIGHT]: { x: windowWidth - debuggerWidth, y: 0 },
  }[anchor];
}

/** handle drag interaction for Network Debugger */
export function useBindDragHandler({
  anchor,
  width,
  onRest,
  localStorageKey,
}: {
  anchor: ControlsAnchor;
  width: number;
  onRest?: () => void;
  localStorageKey?: string;
}) {
  const windowSize = useWindowSize();

  // drag to move
  const [style, api] = useSpring(() => {
    const lsPosition = localStorageKey && parseLsValue(localStorageKey);
    const startPosition = (lsPosition ??
      getStartPosition({
        anchor,
        windowWidth: windowSize.innerWidth,
        debuggerWidth: width,
      })) as { x: number; y: number };

    return {
      ...startPosition,
      height:
        windowSize.innerHeight - startPosition.y - DEBUGGER_PADDING_BOTTOM,
      onRest: () => {
        if (onRest) {
          onRest();
        }
        if (localStorageKey) {
          window.localStorage.setItem(
            localStorageKey,
            JSON.stringify({ x: style.x.get(), y: style.y.get() })
          );
        }
      },
    };
  }, [
    anchor,
    width,
    windowSize.innerWidth,
    windowSize.innerHeight,
    localStorageKey,
    onRest,
  ]);

  const bindDragHandler = useDrag(
    ({ active, xy, previous: [px, py], down }) => {
      const minY = 0;
      const maxY = windowSize.innerHeight - MIN_VISIBLE_PX.y;
      const minX = -windowSize.innerWidth + MIN_VISIBLE_PX.x;
      const maxX = windowSize.innerWidth - MIN_VISIBLE_PX.x;

      const isInit = px === 0 && py === 0;
      const [x_, y_] = xy;
      const [x, y] = [
        x_ + (isInit ? 0 : 0) - width / 2,
        y_ + (isInit ? 0 : 0) - DEBUGGER_HEADER_HEIGHT / 2,
      ];

      const height = windowSize.innerHeight - y - DEBUGGER_PADDING_BOTTOM;
      if (!down) {
        return;
      }
      api.start({
        x: active ? x : Math.min(Math.max(x, minX), maxX),
        y: active ? y : Math.min(Math.max(y, minY), maxY),
        ...(!getIsTouchDevice() && {
          height,
        }),
      });
    },
    [api, windowSize.innerHeight, width]
  );

  // on mount and resize, reset the position to top-left (otherwise, debugger can get stuck outside the viewport if you change window size, e.g. drag debugger to the right, then shrink the window horizontally)
  useMount(() => {
    const startPosition = getStartPosition({
      anchor,
      windowWidth: windowSize.innerWidth,
      debuggerWidth: width,
    }) as { x: number; y: number };
    api.set({
      ...startPosition,
      height:
        windowSize.innerHeight - startPosition.y - DEBUGGER_PADDING_BOTTOM,
    });
  });
  useEvent("resize", () => {
    const startPosition = getStartPosition({
      anchor,
      windowWidth: windowSize.innerWidth,
      debuggerWidth: width,
    }) as { x: number; y: number };
    // can't seem to move the popup after resize but this will fix hidden popup issue after refresh at least
    if (localStorageKey) {
      window.localStorage.setItem(
        localStorageKey,
        JSON.stringify(startPosition)
      );
    }
  });
  const isTop = useMemo(
    () =>
      [ControlsAnchor.TOP_RIGHT, ControlsAnchor.TOP_LEFT].includes(
        anchor as any
      ),
    [anchor]
  );
  return { bindDragHandler, isTop, style };
}

function parseLsValue(localStorageKey: string) {
  try {
    const lsValue = window.localStorage.getItem(localStorageKey);
    return lsValue && JSON.parse(lsValue);
  } catch (error) {
    return null;
  }
}
